import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react"
import { useDispatch } from "react-redux"
import {
	fetchCompteurAssistances,
	fetchConfigAssistances,
	fetchResumeMissions,
} from "../../../store/actions/assistancesActions"
import { fetchResumeCouvertures } from "../../../store/actions/couverturesActions"
import DossierPanneau from "./DossierPanneau"
import { Pastille } from "./assistancesUi"
import PropositionModal from "./PropositionModal"
import MessageModal from "./MessageModal"

// Ordres d'assistance (GE-57) : contexte du dispatch (et de la fiche).
// - résumés « ligne dossier » des cartes de mission, chargés par LOTS
//   (api/assistances/resume-missions) — une carte s'inscrit via
//   useResumeMission(id), sans rien changer aux listes parentes ;
// - panneau Dossier (tiroir) + modals Proposition / E-mail, ouverts depuis
//   n'importe où (carte, onglet, fiche) ;
// - `revision` : incrémentée après chaque action humaine → les vues
//   rechargent.
// Hors provider (autres pages), les hooks rendent null : pas de ligne.
const Ctx = createContext(null)

const POLL_MS = 60 * 1000

export const AssistancesProvider = ({ children }) => {
	const dispatch = useDispatch()
	const [config, setConfig] = useState([])
	const [resumes, setResumes] = useState({})
	// GE-60 : pastilles « ✓ Couvert x.– » (même lot de missions).
	const [couvertures, setCouvertures] = useState({})
	const [dossierId, setDossierId] = useState(null)
	const [proposition, setProposition] = useState(null)
	const [messageId, setMessageId] = useState(null)
	const [revision, setRevision] = useState(0)
	const [compteur, setCompteur] = useState(null)
	// Cartes montées : id → nombre d'abonnés (GE-59 : un Set jamais purgé
	// grossissait jusqu'au 414 nginx).
	const ids = useRef(new Map())
	const sequence = useRef(0)
	const minuteur = useRef(null)
	const monte = useRef(true)
	useEffect(
		() => () => {
			monte.current = false
		},
		[],
	)

	const chargerResumes = useCallback(() => {
		const liste = [...ids.current.keys()]
		if (!liste.length) return Promise.resolve()
		// Requêtes concurrentes : seule la plus récente fait foi.
		const numero = ++sequence.current
		return Promise.all([
			dispatch(fetchResumeMissions(liste)),
			dispatch(fetchResumeCouvertures(liste)),
		]).then(([data, couv]) => {
			if (!monte.current || numero !== sequence.current) return
			if (data) setResumes(data)
			if (couv) setCouvertures(couv)
		})
	}, [dispatch])

	const chargerCompteur = useCallback(
		() =>
			dispatch(fetchCompteurAssistances()).then((c) => {
				if (c && monte.current) setCompteur(c)
			}),
		[dispatch],
	)

	// Inscription d'une carte : un seul appel groupé 300 ms après la vague.
	const inscrire = useCallback(
		(id) => {
			if (!id) return
			const n = ids.current.get(id) || 0
			ids.current.set(id, n + 1)
			if (n > 0) return
			clearTimeout(minuteur.current)
			minuteur.current = setTimeout(chargerResumes, 300)
		},
		[chargerResumes],
	)

	const desinscrire = useCallback((id) => {
		const n = ids.current.get(id) || 0
		if (n <= 1) ids.current.delete(id)
		else ids.current.set(id, n - 1)
	}, [])

	useEffect(() => {
		dispatch(fetchConfigAssistances()).then((data) => {
			if (data && monte.current) setConfig(data)
		})
		chargerCompteur()
		const t = setInterval(() => {
			chargerResumes()
			chargerCompteur()
		}, POLL_MS)
		return () => {
			clearInterval(t)
			clearTimeout(minuteur.current)
		}
	}, [dispatch, chargerResumes, chargerCompteur])

	const actualiser = useCallback(() => {
		setRevision((r) => r + 1)
		chargerResumes()
		chargerCompteur()
	}, [chargerResumes, chargerCompteur])

	const valeur = useMemo(() => {
		const modes = {}
		config.forEach((c) => {
			modes[c.assistance_id] = c.mode
		})
		return {
			resumes,
			couvertures,
			inscrire,
			desinscrire,
			compteur,
			modes,
			// Au moins une assistance observée en ombre (bandeau de l'onglet).
			ombre: config.some((c) => c.mode === "ombre"),
			ombreApplicable: config.some((c) => c.ombre_applicable),
			revision,
			actualiser,
			ouvrirDossier: setDossierId,
			ouvrirProposition: setProposition,
			ouvrirMessage: setMessageId,
		}
	}, [
		config,
		resumes,
		couvertures,
		inscrire,
		desinscrire,
		compteur,
		revision,
		actualiser,
	])

	return (
		<Ctx.Provider value={valeur}>
			{children}
			{dossierId !== null && (
				<DossierPanneau
					dossierId={dossierId}
					onClose={() => setDossierId(null)}
				/>
			)}
			{proposition !== null && (
				<PropositionModal
					proposition={proposition}
					onClose={() => setProposition(null)}
				/>
			)}
			{messageId !== null && (
				<MessageModal
					messageId={messageId}
					onClose={() => setMessageId(null)}
				/>
			)}
		</Ctx.Provider>
	)
}

export const useAssistances = () => useContext(Ctx)

// Résumé « ligne dossier » d'une mission (null hors provider / sans dossier).
export const useResumeMission = (missionId) => {
	const ctx = useContext(Ctx)
	// Fonctions stables seulement en dépendances (pas `ctx`, qui change à
	// chaque réponse) : inscription au montage, désinscription au démontage.
	const inscrire = ctx ? ctx.inscrire : null
	const desinscrire = ctx ? ctx.desinscrire : null
	useEffect(() => {
		if (!inscrire || !missionId) return undefined
		inscrire(missionId)
		return () => desinscrire(missionId)
	}, [inscrire, desinscrire, missionId])
	if (!ctx || !missionId) return null
	return ctx.resumes[missionId] || null
}

// GE-60 : couverture active de la mission ou de sa fiche ({montant,
// assistance, fiche_id}) — même inscription que useResumeMission.
export const useCouvertureMission = (missionId) => {
	const ctx = useContext(Ctx)
	const inscrire = ctx ? ctx.inscrire : null
	const desinscrire = ctx ? ctx.desinscrire : null
	useEffect(() => {
		if (!inscrire || !missionId) return undefined
		inscrire(missionId)
		return () => desinscrire(missionId)
	}, [inscrire, desinscrire, missionId])
	if (!ctx || !missionId) return null
	return ctx.couvertures[missionId] || null
}

// Badge de l'onglet « Assistances (n) » : rendu DANS le provider, rafraîchi
// après chaque action (GE-59).
export const PastilleAssistances = () => {
	const ctx = useContext(Ctx)
	if (!ctx || !ctx.compteur || !ctx.compteur.total) return null
	return (
		<span style={{ marginLeft: 8 }}>
			<Pastille n={ctx.compteur.total} />
		</span>
	)
}
